import React, { useState, useEffect } from 'react';
import OrderCard from './OrderCard';
import { Order } from './order-types';
import { ShippingInfo } from './OrderActions';
import SearchFilterBar from './SearchFilterBar';
import { isWithinInterval, parseISO } from 'date-fns';

interface SalesListProps {
  sales: Order[];
  isUpdating: string | null;
  onViewListing: (listingId: number) => void;
  onViewBuyerProfile: (buyerId: string) => void;
  onContactBuyer: (order: Order) => void;
  onUpdateStatus: (orderId: string, newStatus: string, shippingInfo?: ShippingInfo) => void;
  onCancelOrder?: (orderId: string) => void;
  onConfirmCancellation?: (orderId: string) => void;
  onRejectCancellation?: (orderId: string) => void;
}

const SalesList = ({
  sales,
  isUpdating,
  onViewListing,
  onViewBuyerProfile,
  onContactBuyer,
  onUpdateStatus,
  onCancelOrder,
  onConfirmCancellation,
  onRejectCancellation
}: SalesListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [filteredSales, setFilteredSales] = useState<Order[]>(sales);
  
  // Filter sales based on search term and date range
  useEffect(() => {
    let filtered = [...sales];
    
    // Apply text search
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(sale => 
        sale.listings?.title?.toLowerCase().includes(lowerCaseSearch)
      );
    }
    
    // Apply date filters
    if (startDate || endDate) {
      filtered = filtered.filter(sale => {
        try {
          const saleDate = parseISO(sale.created_at);
          
          // Both start and end dates specified
          if (startDate && endDate) {
            return isWithinInterval(saleDate, { start: startDate, end: endDate });
          } 
          // Only start date specified
          else if (startDate) {
            return saleDate >= startDate;
          } 
          // Only end date specified
          else if (endDate) {
            return saleDate <= endDate;
          }
          return true;
        } catch (error) {
          console.error('Error parsing date:', error);
          return false;
        }
      });
    }
    
    setFilteredSales(filtered);
  }, [sales, searchTerm, startDate, endDate]);
  
  const handleResetFilters = () => {
    setSearchTerm('');
    setStartDate(undefined);
    setEndDate(undefined);
  };
  
  return (
    <div className="space-y-4">
      <SearchFilterBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        startDate={startDate}
        endDate={endDate}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
        onReset={handleResetFilters}
      />
      
      <div className="grid grid-cols-1 gap-4 p-4">
        {filteredSales.map((sale) => (
          <OrderCard
            key={sale.id}
            order={sale}
            isUpdating={isUpdating === sale.id}
            onViewListing={onViewListing}
            onViewBuyerProfile={onViewBuyerProfile}
            onContactBuyer={onContactBuyer}
            onUpdateStatus={onUpdateStatus}
            onCancelOrder={onCancelOrder}
            onConfirmCancellation={onConfirmCancellation}
            onRejectCancellation={onRejectCancellation}
          />
        ))}
        
        {filteredSales.length === 0 && (
          <div className="text-center py-8">
            <p className="text-gray-500">Nuk u gjetën shitje që përputhen me filtrat.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesList;
